import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type EntityDetail as EntityDetailData,
  type EntityMergeHistoryItem,
  type EntityChannelRef,
  type Channel,
  type UpdateEntityRequest,
  type ManualCreateChannelRequest,
} from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { fmtNumber, fmtRelativeDays, platformLabel } from "@/lib/format";
import { EntityPicker } from "@/components/EntityPicker";
import { ChannelPicker } from "@/components/ChannelPicker";

// EntityDetail — v2.8.0 — single-entity view: header + owned-channels
// grid + inbound merge history. Per-channel "移动到 entity…" via
// EntityPicker (Day 6). "编辑" + "+ 添加频道" buttons wired in v2.8.0
// wrap (PATCH /api/entities/{id} + inverse channel picker that calls
// POST /api/channels/{cid}/move-to-entity/{eid}).
//
// Aggregated cross-platform metrics (本周总曝光 / 本周交付) per design
// doc are roadmap items in BACKLOG (no schedule); the placeholder
// section below keeps the visual scaffold while making no version
// commitment.

export default function EntityDetail() {
  const { id } = useParams<{ id: string }>();
  const entityID = Number(id);
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ["entities", "detail", entityID],
    queryFn: () => endpoints.entity(entityID),
    enabled: Number.isFinite(entityID) && entityID > 0,
    refetchOnWindowFocus: false,
  });

  // EntityPicker state — which channel (if any) the operator is moving.
  // null = closed. The channel ref carries the label + the implicit
  // excludeEntityID (= entityID, since these channels currently belong
  // to this entity).
  const [movingChannel, setMovingChannel] = useState<EntityChannelRef | null>(null);
  const [editing, setEditing] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const moveMut = useMutation({
    mutationFn: ({
      channelID,
      toEntityID,
      reason,
    }: {
      channelID: number;
      toEntityID: number;
      reason: string;
    }) =>
      endpoints.moveChannelToEntity(channelID, toEntityID, {
        reason: reason || undefined,
      }),
    onSuccess: (resp) => {
      // Detail page on this side: lose the moved channel (and possibly
      // gain history if move was inbound to here, but here we only see
      // outbound moves so just refetch).
      qc.invalidateQueries({ queryKey: ["entities", "detail", entityID] });
      qc.invalidateQueries({ queryKey: ["entities", "list"] });
      // Destination entity's detail also stale.
      qc.invalidateQueries({ queryKey: ["entities", "detail", resp.to_entity_id] });
      // The channel-detail cache (if open) needs to update its EntityID.
      qc.invalidateQueries({ queryKey: ["channel"] });
      // v2.8.0 Day 7 — also invalidate the EntitySelf cache so moves
      // INTO the Bitbase-self entity propagate without manual reload.
      qc.invalidateQueries({ queryKey: ["entities", "self"] });
    },
  });

  if (!Number.isFinite(entityID) || entityID <= 0) {
    return <NotFound id={id ?? ""} />;
  }
  if (detailQ.isLoading) {
    return <p className="p-6 text-sm text-muted-foreground">加载中…</p>;
  }
  if (detailQ.isError) {
    return (
      <p className="p-6 text-sm text-destructive">
        加载失败:{(detailQ.error as Error).message}
      </p>
    );
  }
  const entity = detailQ.data;
  if (!entity) return <NotFound id={id ?? ""} />;

  return (
    <div className="flex h-full flex-col overflow-auto">
      {/* Header */}
      <div className="border-b border-border bg-background px-6 py-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Link to="/entities" className="text-sm text-muted-foreground hover:underline">
                ← 实体列表
              </Link>
            </div>
            <div className="mt-1 flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{entity.canonical_name}</h1>
              {entity.is_internal && (
                <Badge className="bg-amber-100 text-amber-700">★ 我们</Badge>
              )}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
              {entity.primary_handle && (
                <div className="flex gap-1.5">
                  <dt>primary_handle:</dt>
                  <dd className="font-mono text-foreground">{entity.primary_handle}</dd>
                </div>
              )}
              {entity.entity_type && (
                <div className="flex gap-1.5">
                  <dt>类型:</dt>
                  <dd className="text-foreground">{entity.entity_type}</dd>
                </div>
              )}
              {entity.country && (
                <div className="flex gap-1.5">
                  <dt>地区:</dt>
                  <dd className="text-foreground">{entity.country}</dd>
                </div>
              )}
              <div className="flex gap-1.5">
                <dt>创建:</dt>
                <dd className="text-foreground">{fmtRelativeDays(entity.created_at)}</dd>
              </div>
            </dl>
            {entity.notes && (
              <p className="mt-2 whitespace-pre-wrap rounded border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
                {entity.notes}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              编辑
            </Button>
            <Button size="sm" onClick={() => setAttaching(true)}>
              + 添加频道
            </Button>
          </div>
        </div>
      </div>

      {/* Owned channels grid */}
      <section className="border-b border-border bg-background px-6 py-4">
        <h2 className="mb-3 text-sm font-medium text-muted-foreground">
          拥有的 channels ({entity.channels.length})
        </h2>
        {entity.channels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            还没有频道关联到这个 entity。通过 /channels 的"归属 entity"块移入。
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entity.channels.map((c) => (
              <ChannelCard
                key={c.id}
                channel={c}
                onMove={() => setMovingChannel(c)}
              />
            ))}
          </div>
        )}
      </section>

      <EntityPicker
        open={movingChannel !== null}
        channelLabel={movingChannel?.handle ?? movingChannel?.name ?? `#${movingChannel?.id ?? ""}`}
        excludeEntityID={entity.id}
        onClose={() => setMovingChannel(null)}
        onPick={async (toEntityID, reason) => {
          if (!movingChannel) return;
          await moveMut.mutateAsync({
            channelID: movingChannel.id,
            toEntityID,
            reason,
          });
          setMovingChannel(null);
        }}
      />

      {/* Aggregation roadmap — items in BACKLOG with no scheduled
          delivery. Past versions baked a "Day 8-11 上线" commitment
          into this section that wasn't honest; replaced with the same
          undated phrasing EntitySelf uses. */}
      <section className="border-b border-border bg-background px-6 py-4">
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">
          跨平台聚合(BACKLOG · 暂无排期)
        </h2>
        <p className="text-xs text-muted-foreground">
          本周总曝光 · 总互动 · 交付数等聚合视图,设计在 docs/BACKLOG.md。这一段会在配套查询 ship 时填实。
        </p>
      </section>

      <EditEntityModal
        open={editing}
        entity={entity}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          qc.invalidateQueries({ queryKey: ["entities", "detail", entityID] });
          qc.invalidateQueries({ queryKey: ["entities", "list"] });
          if (entity.is_internal) {
            qc.invalidateQueries({ queryKey: ["entities", "self"] });
          }
        }}
      />

      <AttachChannelModal
        open={attaching}
        targetEntityID={entity.id}
        excludeChannelIDs={entity.channels.map((c) => c.id)}
        onClose={() => setAttaching(false)}
        onAttached={() => {
          setAttaching(false);
          qc.invalidateQueries({ queryKey: ["entities", "detail", entityID] });
          qc.invalidateQueries({ queryKey: ["entities", "list"] });
          qc.invalidateQueries({ queryKey: ["channel"] });
          if (entity.is_internal) {
            qc.invalidateQueries({ queryKey: ["entities", "self"] });
          }
        }}
      />

      {/* Merge history */}
      <section className="bg-background px-6 py-4">
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">
          合并历史 ({entity.merge_history.length})
        </h2>
        {entity.merge_history.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            还没有合并记录。运营通过 /channels 详情页"移动到 entity..."合并后会出现在这里。
          </p>
        ) : (
          <ul className="space-y-2 text-sm">
            {entity.merge_history.map((h) => (
              <MergeHistoryRow key={h.history_id} item={h} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ChannelCard({
  channel,
  onMove,
}: {
  channel: EntityChannelRef;
  onMove: () => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span>{platformLabel(channel.platform)}</span>
          {channel.lifecycle_status === "active" ? (
            <Badge variant="secondary">{channel.lifecycle_status}</Badge>
          ) : (
            <Badge>{channel.lifecycle_status}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <div className="font-mono text-xs text-muted-foreground">
          {channel.handle ?? channel.platform_channel_id}
        </div>
        <div className="font-medium">{channel.name ?? "—"}</div>
        {channel.follower_count !== undefined && (
          <div className="text-xs text-muted-foreground">
            {fmtNumber(channel.follower_count)} 粉丝
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Link
            to={`/channels/${channel.id}`}
            className="text-xs text-primary hover:underline"
          >
            详情 →
          </Link>
          <button
            type="button"
            onClick={onMove}
            className="ml-auto text-xs text-primary hover:underline"
          >
            移动到 entity…
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function MergeHistoryRow({ item }: { item: EntityMergeHistoryItem }) {
  const channelLabel =
    item.channel_handle ?? item.channel_name ?? `channel #${item.channel_id}`;
  const fromLabel = item.from_entity_canonical_name
    ? `${item.from_entity_canonical_name} (entity #${item.from_entity_id})`
    : `entity #${item.from_entity_id}`;
  return (
    <li className="flex items-baseline gap-2 text-sm">
      <span className="font-mono text-xs text-muted-foreground">
        {fmtRelativeDays(item.created_at)}
      </span>
      <span>
        <Link
          to={`/channels/${item.channel_id}`}
          className="font-medium text-foreground hover:underline"
        >
          {channelLabel}
        </Link>{" "}
        <span className="text-muted-foreground">由 {fromLabel} 移入</span>
        <span className="text-muted-foreground"> · by {item.actor}</span>
        {item.reason && (
          <span className="text-muted-foreground"> · {item.reason}</span>
        )}
      </span>
    </li>
  );
}

// EditEntityModal — operator-facing form for PATCH /api/entities/{id}.
// Pre-fills from the current entity, sends only the fields the operator
// touched. is_internal toggle is editable so the Day 7 NotSeeded
// recovery flow ("wrong checkbox on create") doesn't need SQL.
function EditEntityModal({
  open,
  entity,
  onClose,
  onSaved,
}: {
  open: boolean;
  entity: EntityDetailData;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [canonical, setCanonical] = useState(entity.canonical_name);
  const [handle, setHandle] = useState(entity.primary_handle ?? "");
  const [isInternal, setIsInternal] = useState(entity.is_internal);
  const [notes, setNotes] = useState(entity.notes ?? "");
  const [err, setErr] = useState<string | null>(null);

  // Reset form whenever the modal opens for a (potentially) new entity.
  useState(() => undefined);

  const saveMut = useMutation({
    mutationFn: (body: UpdateEntityRequest) =>
      endpoints.updateEntity(entity.id, body),
    onSuccess: onSaved,
    onError: (e) => setErr((e as Error).message),
  });

  if (!open) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const body: UpdateEntityRequest = {};
    const trimmedCanonical = canonical.trim();
    if (trimmedCanonical !== entity.canonical_name) {
      if (!trimmedCanonical) {
        setErr("canonical_name 不能为空");
        return;
      }
      body.canonical_name = trimmedCanonical;
    }
    const trimmedHandle = handle.trim();
    if (trimmedHandle !== (entity.primary_handle ?? "")) {
      body.primary_handle = trimmedHandle; // "" = clear to NULL
    }
    const trimmedNotes = notes.trim();
    if (trimmedNotes !== (entity.notes ?? "")) {
      body.notes = trimmedNotes;
    }
    if (isInternal !== entity.is_internal) {
      body.is_internal = isInternal;
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    saveMut.mutate(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-4 shadow-xl">
        <h2 className="mb-3 text-base font-semibold">编辑 entity #{entity.id}</h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs text-muted-foreground">名称 *</label>
            <Input
              autoFocus
              value={canonical}
              onChange={(e) => setCanonical(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground">
              primary handle(留空清除)
            </label>
            <Input
              placeholder="@something"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
            />
            <span>
              is_internal(Bitbase 自身)— 翻转后影响 /entities/self resolve 哪一个
            </span>
          </label>
          <div>
            <label className="block text-xs text-muted-foreground">备注</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            />
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" type="button" onClick={onClose}>
              取消
            </Button>
            <Button size="sm" type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? "保存中…" : "保存"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// AttachChannelModal — two-mode modal for adding a channel to this
// entity. Mode A ("已有") picks from the existing /channels library;
// mode B ("新建") creates a brand-new channel via the manual endpoint
// AND attaches it in one click, so operators don't have to bounce
// to /channels and come back. Both paths end in
// moveChannelToEntity → the entity's detail refetches.
//
// New-channel flow: POST /api/manual/channels creates the row; the
// migration 026 BEFORE INSERT trigger auto-creates a 1:1 entity for
// it. The follow-up move-to-entity call shifts the channel under the
// operator's intended target; the trigger-created intermediate
// entity becomes orphan (no remaining channels) and the orphan
// cleanup CTE deletes it in the same statement. Net: 1 channel + 1
// entity in the DB, exactly as if the operator had created the
// channel pre-attached.
type AttachMode = "existing" | "new";

function AttachChannelModal({
  open,
  targetEntityID,
  excludeChannelIDs,
  onClose,
  onAttached,
}: {
  open: boolean;
  targetEntityID: number;
  excludeChannelIDs: number[];
  onClose: () => void;
  onAttached: () => void;
}) {
  const [mode, setMode] = useState<AttachMode>("existing");
  // Existing-channel state
  const [pickedChannelID, setPickedChannelID] = useState<number | null>(null);
  const [pickedChannel, setPickedChannel] = useState<Channel | null>(null);
  const [reason, setReason] = useState("");
  // New-channel state
  const [newPlatform, setNewPlatform] = useState<"youtube" | "twitter" | "tiktok" | "rss" | "foresight" | "panews">("youtube");
  const [newURL, setNewURL] = useState("");
  const [newName, setNewName] = useState("");
  const [newContentType, setNewContentType] = useState("");
  const [newRegion, setNewRegion] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const attachExistingMut = useMutation({
    mutationFn: () =>
      endpoints.moveChannelToEntity(pickedChannelID!, targetEntityID, {
        reason: reason.trim() || undefined,
      }),
    onSuccess: onAttached,
    onError: (e) => setErr((e as Error).message),
  });

  // createAndAttach: 2 sequential calls. If the first succeeds and the
  // second fails, the channel + its trigger-attached 1:1 entity still
  // exist in the library; operator can retry the attach via "已有"
  // mode without re-creating. So failure mid-flow is recoverable, not
  // data-corrupting.
  const createAndAttachMut = useMutation({
    mutationFn: async (req: ManualCreateChannelRequest) => {
      const ch = await endpoints.createManualChannel(req);
      await endpoints.moveChannelToEntity(ch.id, targetEntityID, {
        reason: reason.trim() || undefined,
      });
      return ch;
    },
    onSuccess: onAttached,
    onError: (e) => setErr((e as Error).message),
  });

  if (!open) return null;

  const excluded = new Set(excludeChannelIDs);

  const handleSubmit = () => {
    setErr(null);
    if (mode === "existing") {
      if (!pickedChannelID) return;
      if (excluded.has(pickedChannelID)) {
        setErr("这个 channel 已经在当前 entity 下了");
        return;
      }
      attachExistingMut.mutate();
      return;
    }
    // mode === "new"
    const url = newURL.trim();
    const name = newName.trim();
    if (!url || !name) {
      setErr("URL/handle 和 名称 都是必填");
      return;
    }
    const body: ManualCreateChannelRequest = {
      platform: newPlatform,
      url_or_handle: url,
      name,
    };
    const ct = newContentType.trim();
    if (ct) body.content_type = ct;
    const region = newRegion.trim();
    if (region) body.region = region;
    createAndAttachMut.mutate(body);
  };

  const submitting = attachExistingMut.isPending || createAndAttachMut.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-xl rounded-lg border border-border bg-background p-4 shadow-xl">
        <h2 className="mb-3 text-base font-semibold">添加 channel 到此 entity</h2>

        {/* Tab switcher */}
        <div className="mb-3 flex gap-1 rounded-md border border-border bg-muted/40 p-0.5 text-xs">
          <TabPill active={mode === "existing"} onClick={() => { setMode("existing"); setErr(null); }}>
            已有 channel
          </TabPill>
          <TabPill active={mode === "new"} onClick={() => { setMode("new"); setErr(null); }}>
            新建 channel
          </TabPill>
        </div>

        {mode === "existing" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              从频道库选一个 channel,移动到本 entity。
            </p>
            <ChannelPicker
              value={pickedChannelID}
              onChange={(id, ch) => {
                setPickedChannelID(id);
                setPickedChannel(ch);
                setErr(null);
              }}
              placeholder="搜频道名 / handle / ID…"
            />
            {pickedChannel && excluded.has(pickedChannel.ID) && (
              <p className="text-xs text-amber-700">
                这个 channel 已经属于当前 entity,选另一个。
              </p>
            )}
          </div>
        )}

        {mode === "new" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              新建一个 channel 并直接挂到本 entity。等同于先在 /channels 加号再回这里"移动",一步完成。
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs text-muted-foreground">平台 *</label>
                <select
                  value={newPlatform}
                  onChange={(e) => setNewPlatform(e.target.value as typeof newPlatform)}
                  className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
                >
                  <option value="youtube">YouTube</option>
                  <option value="twitter">X (Twitter)</option>
                  <option value="tiktok">TikTok</option>
                  <option value="rss">RSS / Medium / Substack</option>
                  <option value="foresight">Foresight News (专栏)</option>
                  <option value="panews">PA News (专栏)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground">名称 *</label>
                <Input
                  placeholder="例如:Bitbase 中文 X"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground">URL / handle *</label>
              <Input
                placeholder={
                  newPlatform === "youtube"
                    ? "https://youtube.com/@bitbasecom 或 @bitbasecom"
                    : newPlatform === "twitter"
                    ? "https://x.com/BitbaseCN 或 @BitbaseCN"
                    : newPlatform === "tiktok"
                    ? "https://tiktok.com/@bitbase 或 @bitbase"
                    : newPlatform === "rss"
                    ? "https://medium.com/feed/@bitbase 或 https://name.substack.com/feed"
                    : newPlatform === "foresight"
                    ? "https://foresightnews.pro/column/detail/1316"
                    : "https://www.panewslab.com/zh/columns/019d6678-2e81-77b8-a215-4dc26d179519"
                }
                value={newURL}
                onChange={(e) => setNewURL(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs text-muted-foreground">内容类型(可选)</label>
                <Input
                  placeholder="例如:research / news / tutorial"
                  value={newContentType}
                  onChange={(e) => setNewContentType(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground">区域(可选)</label>
                <Input
                  placeholder="例如:CN / Global"
                  value={newRegion}
                  onChange={(e) => setNewRegion(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <div className="mt-3">
          <label className="block text-xs text-muted-foreground">
            原因(可选,合并审计用)
          </label>
          <Input
            placeholder="例如:补充 Bitbase 的 X 中文账号"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {err && <p className="mt-2 text-xs text-destructive">{err}</p>}

        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" type="button" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting || (mode === "existing" && (!pickedChannelID || (pickedChannel ? excluded.has(pickedChannel.ID) : false)))}>
            {submitting
              ? mode === "existing" ? "移动中…" : "创建中…"
              : mode === "existing" ? "确认添加" : "创建并挂到此 entity"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TabPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "flex-1 rounded bg-background px-2.5 py-1 font-medium text-foreground shadow-sm"
          : "flex-1 rounded px-2.5 py-1 text-muted-foreground hover:text-foreground"
      }
    >
      {children}
    </button>
  );
}

function NotFound({ id }: { id: string }) {
  return (
    <div className="p-6">
      <p className="text-sm text-muted-foreground">
        entity #{id} 不存在或已被删除。
      </p>
      <Link to="/entities" className="text-sm text-primary hover:underline">
        ← 返回实体列表
      </Link>
    </div>
  );
}
